<template>
  <v-container fluid class="pa-0 fill-height d-flex flex-column">
    <!-- Top Toolbar -->
    <v-app-bar flat color="#1E1E1E" dark class="border-bottom px-4 pt-2" height="auto">
      <v-row align="center" no-gutters>
        <v-col cols="12" md="3" class="pr-4 mb-2 mb-md-0">
          <h2 class="text-h5 font-weight-bold white--text">ToDo Dashboard</h2>
        </v-col>

        <v-col cols="12" md="9" class="d-flex flex-wrap align-center justify-md-end">
          <!-- Studio Selector -->
          <div class="d-flex align-center mr-4 mb-2 mb-md-0" v-if="userService.hasRight('todo_changeStudio')">
            <v-icon color="grey" class="mr-2">mdi-domain</v-icon>
            <v-select
              dense
              outlined
              hide-details
              dark
              :items="studios"
              v-model="selectedStudio"
              item-text="name"
              item-value="_id"
              @change="reloadTodos"
              label="Studio"
              style="max-width: 200px; width: 100%"></v-select>
          </div>

          <!-- My ToDos Toggle -->
          <v-chip
            v-if="userService.hasRight('todo_fetchAndEdit')"
            filter
            :input-value="onlyUserTodos"
            @click="toggleOnlyUserTodos"
            class="mr-4 mb-2 mb-md-0"
            color="primary"
            outlined
            dark>
            <v-icon left>mdi-account</v-icon>
            Meine ToDos
          </v-chip>

          <!-- Sort Menu -->
          <v-menu offset-y dark>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" text dark class="mr-2 mb-2 mb-md-0"> <v-icon left>mdi-sort</v-icon> Sortieren </v-btn>
            </template>
            <v-list dense dark color="#1E1E1E">
              <v-list-item @click="setSort('priority')">
                <v-list-item-title :class="{ 'font-weight-bold': sortBy === 'priority' }">Priorität</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setSort('date')">
                <v-list-item-title :class="{ 'font-weight-bold': sortBy === 'date' }">Datum (Neu zuerst)</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- View Toggle -->
          <v-btn icon dark @click="viewMode = viewMode === 'grid' ? 'list' : 'grid'" class="mr-4 mb-2 mb-md-0">
            <v-icon>{{ viewMode === "grid" ? "mdi-view-list" : "mdi-view-grid" }}</v-icon>
          </v-btn>

          <!-- Add Button -->
        </v-col>
        <v-col align="center" class="my-5" cols="12">
          <v-btn color="primary" class="mb-2 mb-md-0" @click="openCreateDialog" elevation="0">
            <v-icon left>mdi-plus</v-icon>
            Neues ToDo erfassen
          </v-btn>
        </v-col>
      </v-row>
    </v-app-bar>

    <!-- Tabs & Content -->
    <v-container fluid class="flex-grow-1 overflow-y-auto pa-4">
      <v-tabs centered v-model="activeTab" background-color="transparent" dark color="primary" class="mb-4 rounded-lg">
        <v-tab href="#techniker" class="text-capitalize">Techniker</v-tab>
        <v-tab href="#marketing" class="text-capitalize">Marketing</v-tab>
        <v-tab v-if="userService.hasRight('coo_toDoListItems')" href="#coo" class="text-capitalize">COO</v-tab>
        <v-tab v-if="userService.hasRight('cl_toDoListItems')" href="#cl" class="text-capitalize">CL</v-tab>
        <v-tab v-if="userService.hasRight('it_toDoListItems')" href="#it" class="text-capitalize">IT</v-tab>
        <v-tab href="#resolved" class="text-capitalize">Erledigt</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" style="background-color: transparent" dark>
        <!-- Generic Tab Content Generator -->
        <v-tab-item v-for="category in ['techniker', 'marketing', 'coo', 'cl', 'it']" :key="category" :value="category">
          <!-- Search Bar -->
          <v-row class="mb-4" v-if="shouldShowCategory(category)">
            <v-col cols="12" md="4" class="ml-auto">
              <v-text-field
                v-model="searchQueries[category]"
                dense
                outlined
                rounded
                dark
                hide-details
                placeholder="Suchen..."
                prepend-inner-icon="mdi-magnify"
                clearable></v-text-field>
            </v-col>
          </v-row>

          <!-- Card Grid -->
          <v-row v-if="shouldShowCategory(category) && viewMode === 'grid'">
            <v-col v-for="item in getSortedTodos(category)" :key="item._id" cols="12" sm="6" md="6" lg="4" xl="3">
              <v-card
                elevation="2"
                class="rounded-lg fill-height d-flex flex-column transition-swing"
                hover
                dark
                color="#1E1E1E"
                @click="openDetailDialog(item)"
                :style="`border-left: 6px solid ${getPriorityColorHex(item.priority)};`">
                <v-card-title class="subtitle-1 font-weight-bold pt-3 pb-1 text-truncate d-block pr-5 position-relative">
                  {{ item.title }}
                  <v-icon v-if="item.inProgress" color="orange" small style="position: absolute; top: 12px; right: 12px">mdi-progress-pencil</v-icon>
                </v-card-title>

                <v-card-subtitle class="pb-2 pt-1 d-flex flex-wrap align-center">
                  <span class="caption grey--text mr-2">{{ $moment(item.createdAt).format("DD.MM.YY") }}</span>
                  <v-chip x-small outlined dark v-if="item.studio" color="blue" class="mr-1">{{ item.studio.name }}</v-chip>
                  <v-chip x-small label dark v-if="item.subcategory" color="orange lighten-4" text-color="orange darken-3" class="font-weight-bold">{{
                    item.subcategory
                  }}</v-chip>
                </v-card-subtitle>

                <v-divider class="mx-4 opacity-50"></v-divider>

                <v-card-text class="flex-grow-1 pt-3">
                  <div class="text-body-2 white--text text-truncate-3-lines" style="min-height: 4.5em">
                    {{ item.description }}
                  </div>

                  <div class="mt-4 d-flex align-center justify-space-between">
                    <div class="d-flex align-center caption grey--text">
                      <v-avatar size="24" color="grey darken-2" class="mr-2">
                        <span class="white--text font-weight-bold" style="font-size: 10px"
                          >{{ item.createdBy.vorname[0] }}{{ item.createdBy.nachname[0] }}</span
                        >
                      </v-avatar>
                      {{ item.createdBy.vorname }}
                    </div>
                    <div v-if="item.assignedUsers && item.assignedUsers.length > 0" class="caption blue--text">
                      <v-icon x-small color="blue" class="mr-1">mdi-account-multiple</v-icon> {{ item.assignedUsers.length }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- List View -->
          <v-data-table
            v-if="shouldShowCategory(category) && viewMode === 'list'"
            :headers="headers"
            :items="getSortedTodos(category)"
            :items-per-page="-1"
            dark
            class="elevation-1 rounded-lg"
            @click:row="openDetailDialog"
            style="cursor: pointer; background-color: #1e1e1e">
            <template v-slot:item.priority="{ item }">
              <v-chip small :color="getPriorityColor(item.priority)" dark>{{ mapPriorityName(item.priority) }}</v-chip>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ $moment(item.createdAt).format("DD.MM.YY HH:mm") }}
            </template>
            <template v-slot:item.createdBy="{ item }"> {{ item.createdBy.vorname }} {{ item.createdBy.nachname }} </template>
            <template v-slot:item.assignedUsers="{ item }">
              <span v-if="item.assignedUsers.length">{{ item.assignedUsers.length }} User</span>
              <span v-else>-</span>
            </template>
          </v-data-table>

          <v-alert v-if="shouldShowCategory(category) && getSortedTodos(category).length === 0" type="info" text dark class="mt-4 rounded-lg">
            Keine ToDos in dieser Kategorie gefunden.
          </v-alert>
        </v-tab-item>

        <!-- Resolved Tab -->
        <v-tab-item value="resolved">
          <v-row class="mb-4">
            <v-col cols="12" md="4" class="ml-auto">
              <v-text-field
                v-model="searchQueries['resolved']"
                dense
                outlined
                rounded
                dark
                hide-details
                placeholder="Suche in erledigten..."
                prepend-inner-icon="mdi-magnify"
                clearable></v-text-field>
            </v-col>
          </v-row>

          <div v-if="groupedResolvedTodosFiltered.length === 0" class="d-flex justify-center mt-10">
            <v-alert type="info" text dark>Keine erledigten ToDo's gefunden</v-alert>
          </div>

          <v-timeline dense align-top v-else dark>
            <div v-for="yearGroup in groupedResolvedTodosFiltered" :key="yearGroup.year">
              <v-timeline-item hide-dot class="pb-0" dark>
                <span class="text-h6 font-weight-bold grey--text">{{ yearGroup.year }}</span>
              </v-timeline-item>

              <div v-for="monthGroup in yearGroup.months" :key="monthGroup.monthName">
                <v-timeline-item small color="grey lighten-1" dark>
                  <template v-slot:opposite>
                    <span class="font-weight-bold">{{ monthGroup.monthName }}</span>
                  </template>
                  <v-card class="elevation-1 rounded-lg mb-4" color="#1E1E1E" dark>
                    <v-card-title class="subtitle-2 grey darken-3 py-2"> {{ monthGroup.monthName }} ({{ monthGroup.totalCount }}) </v-card-title>
                    <v-card-text class="pa-0">
                      <v-expansion-panels accordion flat dark>
                        <v-expansion-panel v-if="monthGroup.techniker.length > 0" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >Techniker ({{ monthGroup.techniker.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.techniker"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <!-- Repeat for other categories in resolved view -->
                        <v-expansion-panel v-if="monthGroup.marketing.length > 0" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >Marketing ({{ monthGroup.marketing.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.marketing"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.coo.length > 0 && userService.hasRight('coo_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >COO ({{ monthGroup.coo.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.coo"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.cl.length > 0 && userService.hasRight('cl_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >CL ({{ monthGroup.cl.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.cl"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel v-if="monthGroup.it.length > 0 && userService.hasRight('it_toDoListItems')" class="grey darken-4">
                          <v-expansion-panel-header class="caption font-weight-bold grey--text"
                            >IT ({{ monthGroup.it.length }})</v-expansion-panel-header
                          >
                          <v-expansion-panel-content>
                            <v-list dense dark color="#1E1E1E">
                              <v-list-item
                                v-for="todo in monthGroup.it"
                                :key="todo._id"
                                @click="openDetailDialog(todo)"
                                class="rounded-lg mb-1 hover-bg">
                                <v-list-item-content>
                                  <v-list-item-title class="font-weight-medium">{{ todo.title }}</v-list-item-title>
                                  <v-list-item-subtitle class="grey--text">{{ formatDate(todo.createdAt) }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </div>
            </div>
          </v-timeline>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="todoDialog" max-width="700px" persistent transition="dialog-bottom-transition" dark>
      <v-card class="rounded-xl" dark color="#1E1E1E">
        <v-toolbar color="#2C2C2C" dark flat>
          <v-toolbar-title>
            <span class="text-h6 font-weight-bold white--text">{{ todoDialogTitle }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeTodoDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-6">
          <v-form ref="todoForm" v-model="formValid">
            <v-row>
              <v-col cols="12">
                <user-search
                  :value="createdByUser"
                  :key="assignedUserKey"
                  ignoreRights
                  instructor
                  label="Erstellt von"
                  @update="updateCreatedByUser"
                  outlined
                  dense
                  dark></user-search>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedTodo.category"
                  :items="availableCategories"
                  label="Kategorie"
                  outlined
                  dense
                  dark
                  :disabled="isCategoryDisabled(editedTodo.category)"></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-if="editedTodo.category === 'marketing'"
                  v-model="editedTodo.subcategory"
                  :items="['Umbau', 'Events', 'News', 'Sonstiges']"
                  label="Unterkategorie"
                  outlined
                  dense
                  dark></v-select>
                <v-select
                  v-else-if="editedTodo.category === 'coo' && userService.hasRight('coo_toDoListItems')"
                  v-model="editedTodo.subcategory"
                  :items="sortedCooSubcategories"
                  label="Unterkategorie"
                  outlined
                  dense
                  dark></v-select>
                <v-select
                  v-else-if="editedTodo.category === 'cl' && userService.hasRight('cl_toDoListItems')"
                  v-model="editedTodo.subcategory"
                  :items="sortedClSubcategories"
                  label="Unterkategorie"
                  outlined
                  dense
                  dark></v-select>
                <v-select
                  v-else-if="editedTodo.category === 'it' && userService.hasRight('it_toDoListItems')"
                  v-model="editedTodo.subcategory"
                  :items="sortedITSubcategories"
                  label="Unterkategorie"
                  outlined
                  dense
                  dark></v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedTodo.title" label="Titel" :rules="[(v) => !!v || 'Pflichtfeld']" outlined dense dark></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="editedTodo.description" label="Beschreibung" outlined rows="4" dark></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-if="canEditPriority()"
                  v-model="editedTodo.priority"
                  :items="priorityItems"
                  label="Priorität"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld']">
                  <template v-slot:selection="{ item }">
                    <v-chip small :color="getPriorityColor(item)" dark>{{ mapPriorityName(item) }}</v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-chip small :color="getPriorityColor(item)" dark>{{ mapPriorityName(item) }}</v-chip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-if="userService.hasRight('todo_changeStudio')"
                  :items="studios"
                  label="Studio"
                  v-model="editedTodo.studio"
                  item-text="name"
                  item-value="_id"
                  outlined
                  dense
                  dark></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey lighten-1" @click="closeTodoDialog">Abbrechen</v-btn>
          <v-btn color="primary" elevation="0" @click="saveTodo" :disabled="!formValid">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" fullscreen hide-overlay transition="dialog-bottom-transition" dark>
      <v-card dark>
        <v-toolbar dark color="#2C2C2C">
          <v-btn icon dark @click="closeDetailDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>ToDo Details</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="toggleEditMode">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            class="mr-2"
            v-if="currentTodo?.category !== 'coo' && currentTodo?.category !== 'cl' && currentTodo?.category !== 'it'"
            @click="deleteCurrentTodo">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-toolbar>

        <v-container fluid class="pa-0 fill-height align-start">
          <v-row class="fill-height ma-0">
            <!-- Left Column: Details -->
            <v-col cols="12" md="5" lg="4" class="fill-height overflow-y-auto pa-6 border-right" style="background-color: #1e1e1e">
              <!-- Edit Mode Form -->
              <div v-if="editMode">
                <h3 class="text-h6 mb-4 white--text">Bearbeiten</h3>
                <v-text-field v-model="editedTodo.title" label="Titel" outlined dense dark></v-text-field>
                <v-textarea v-model="editedTodo.description" label="Beschreibung" outlined rows="5" dark></v-textarea>
                <v-select label="Kategorie" v-model="editedTodo.category" :items="availableCategories" outlined dense disabled dark></v-select>
                <v-select label="Priorität" v-model="editedTodo.priority" :items="priorityItems" outlined dense dark>
                  <template v-slot:selection="{ item }">
                    <v-chip small :color="getPriorityColor(item)" dark>{{ mapPriorityName(item) }}</v-chip>
                  </template>
                </v-select>
                <v-select
                  v-if="userService.hasRight('todo_changeStudio')"
                  :items="studios"
                  label="Studio"
                  v-model="editedTodo.studio"
                  item-text="name"
                  item-value="_id"
                  outlined
                  dense
                  dark></v-select>

                <div class="d-flex justify-end mt-4">
                  <v-btn text color="grey" class="mr-2" @click="toggleEditMode">Abbrechen</v-btn>
                  <v-btn color="primary" @click="saveEdit">Speichern</v-btn>
                </div>
              </div>

              <!-- View Mode -->
              <div v-else>
                <div class="d-flex align-center mb-2">
                  <v-chip small :color="currentTodo?.resolved ? 'green' : 'orange'" dark class="mr-2">
                    {{ currentTodo?.resolved ? "Erledigt" : "Offen" }}
                  </v-chip>
                  <v-chip small outlined dark color="grey lighten-1" class="mr-2">{{ formatDate(currentTodo?.createdAt) }}</v-chip>
                  <v-chip small :color="getPriorityColor(currentTodo?.priority)" dark>{{ mapPriorityName(currentTodo?.priority) }}</v-chip>
                </div>

                <h2 class="text-h5 font-weight-bold mb-4 white--text">
                  {{ currentTodo?.title }}
                  <v-btn icon small dark @click="translateTitle" color="blue lighten-2"><v-icon small>mdi-translate</v-icon></v-btn>
                </h2>

                <v-card outlined class="mb-4 pa-4 rounded-lg" style="background-color: #2c2c2c; border-color: #424242" dark>
                  <div class="text-body-1 white--text" style="white-space: pre-wrap">{{ currentTodo?.description }}</div>
                  <div class="d-flex justify-end mt-2">
                    <v-btn icon small dark @click="copyTextToClipboard(currentTodo?.description)"><v-icon small>mdi-content-copy</v-icon></v-btn>
                    <v-btn icon small dark @click="translateText" color="blue lighten-2"><v-icon small>mdi-translate</v-icon></v-btn>
                  </div>
                </v-card>

                <v-divider class="mb-4" dark></v-divider>

                <div class="mb-4">
                  <div class="text-subtitle-2 grey--text mb-1">Erstellt von</div>
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="mr-2 white--text">
                      {{ currentTodo?.createdBy?.vorname[0] }}{{ currentTodo?.createdBy?.nachname[0] }}
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium white--text">{{ currentTodo?.createdBy?.vorname }} {{ currentTodo?.createdBy?.nachname }}</div>
                      <div class="caption grey--text">{{ currentTodo?.studio?.name }}</div>
                    </div>
                  </div>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <div class="text-subtitle-2 grey--text">Zugewiesen an</div>
                    <v-btn icon small color="primary" @click="toggleAssignment" v-if="userService.hasRight('todo_manageUsers')"
                      ><v-icon>mdi-account-plus</v-icon></v-btn
                    >
                  </div>

                  <v-expand-transition>
                    <div v-show="showAssignment" class="mb-3 pa-3 grey darken-3 rounded-lg">
                      <user-search
                        :value="newAssignedUser"
                        :key="assignedUserKey"
                        ignoreRights
                        label="Benutzer suchen"
                        @update="updateNewAssignedUser"
                        dense
                        outlined
                        dark
                        class="mb-2"></user-search>
                      <v-btn block small color="primary" @click="submitAssignment" :disabled="!newAssignedUser">Zuweisen</v-btn>
                    </div>
                  </v-expand-transition>

                  <div class="d-flex flex-wrap">
                    <v-chip
                      v-for="(u, i) in currentTodo?.assignedUsers"
                      :key="i"
                      class="mr-2 mb-2"
                      close
                      dark
                      @click:close="removeUser(u._id)"
                      small
                      outlined>
                      <v-avatar left color="grey darken-3" class="white--text">{{ u.user.vorname[0] }}</v-avatar>
                      {{ u.user.vorname }} {{ u.user.nachname }}
                    </v-chip>
                    <span v-if="!currentTodo?.assignedUsers?.length" class="caption grey--text font-italic">Niemand zugewiesen</span>
                  </div>
                </div>

                <v-divider class="mb-4" dark></v-divider>

                <!-- Attachments -->
                <h3 class="text-subtitle-1 font-weight-bold mb-2 white--text">Anhänge</h3>
                <v-row dense>
                  <v-col cols="6" v-for="(att, idx) in currentTodoAttachments" :key="idx">
                    <v-card
                      outlined
                      class="pa-2 d-flex align-center"
                      color="#2C2C2C"
                      dark
                      @click="att.type === 'image' ? openAttachementDialog(idx) : openAttachment(att)">
                      <v-icon class="mr-2" large color="primary">{{ getAttachmentIcon(att.type) }}</v-icon>
                      <div class="text-truncate caption white--text">{{ att.originalName }}</div>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small color="red" @click.stop="deleteSelectedAttachment(att._id)"><v-icon>mdi-close</v-icon></v-btn>
                    </v-card>
                  </v-col>
                </v-row>

                <div class="d-flex mt-3">
                  <v-btn small outlined dark class="mr-2 flex-grow-1" @click="triggerFileUpload"
                    ><v-icon left small>mdi-paperclip</v-icon>Datei</v-btn
                  >
                  <v-btn small outlined dark class="flex-grow-1" @click="videoLinkDialog = true"><v-icon left small>mdi-video</v-icon>Link</v-btn>
                </div>
                <!-- Hidden File Input -->
                <v-file-input ref="fileInput" v-model="selectedFile" style="display: none" @change="onFileSelected"></v-file-input>
              </div>
            </v-col>

            <!-- Right Column: Comments & Actions -->
            <v-col cols="12" md="7" lg="8" class="fill-height d-flex flex-column pa-0">
              <div class="flex-grow-1 overflow-y-auto pa-4" style="max-height: calc(100vh - 250px)">
                <div v-if="currentTodo?.comments?.length === 0" class="text-center grey--text mt-10">Keine Kommentare vorhanden.</div>
                <div v-for="(comment, index) in currentTodo?.comments" :key="index" class="mb-3 d-flex flex-column">
                  <div class="d-flex align-center mb-1" :class="{ 'justify-end': isCurrentUser(comment.user._id) }">
                    <span class="caption grey--text mr-2" v-if="!isCurrentUser(comment.user._id)"
                      >{{ comment.user.vorname }} {{ comment.user.nachname }}</span
                    >
                    <span class="caption grey--text">{{ formatDate(comment.createdAt) }}</span>
                  </div>
                  <v-card
                    :color="isCurrentUser(comment.user._id) ? 'primary' : '#2C2C2C'"
                    dark
                    class="rounded-lg pa-3 elevation-1"
                    :class="{ 'align-self-end': isCurrentUser(comment.user._id), 'align-self-start': !isCurrentUser(comment.user._id) }"
                    style="max-width: 80%">
                    <div class="text-body-2 white--text">{{ comment.comment }}</div>
                    <v-chip x-small v-if="comment.marketingOnly" color="orange" dark class="mt-1">Marketing Only</v-chip>
                    <div class="text-right mt-1" v-if="comment.status && comment.status !== 'none'">
                      <v-chip x-small outlined dark>{{ comment.status }}</v-chip>
                    </div>
                  </v-card>
                </div>
              </div>

              <!-- Action Area -->
              <v-card flat class="pa-4 border-top" color="#1E1E1E" dark>
                <v-row v-if="currentTodo?.category == 'cl' || currentTodo?.category == 'it'" class="mb-2">
                  <v-col cols="6">
                    <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="currentTodo.plannedDate"
                          label="Planung (Datum)"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          dark></v-text-field>
                      </template>
                      <v-date-picker v-model="currentTodo.plannedDate" @input="dateMenu = false" dark></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu v-model="timeMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="currentTodo.plannedTime"
                          label="Planung (Zeit)"
                          prepend-icon="mdi-clock"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          dense
                          dark></v-text-field>
                      </template>
                      <v-time-picker v-model="currentTodo.plannedTime" @input="timeMenu = false" format="24hr" dark></v-time-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="newComment"
                  outlined
                  rows="2"
                  label="Schreibe einen Kommentar..."
                  hide-details
                  class="rounded-lg"
                  dark></v-textarea>

                <div class="d-flex align-center justify-space-between mt-3">
                  <div class="d-flex align-center">
                    <v-switch v-model="isResolved" label="Erledigen" dense hide-details class="mr-4" dark></v-switch>
                    <v-checkbox
                      v-if="currentTodo?.category === 'marketing' && userService.hasRight('todo_fetchAllMarketing')"
                      v-model="isMarketingOnlyComment"
                      label="Marketing Intern"
                      dense
                      hide-details
                      dark></v-checkbox>
                    <v-switch
                      v-if="currentTodo?.category === 'marketing' && userService.hasRight('todo_fetchAllMarketing')"
                      v-model="currentTodo.inProgress"
                      @change="updateInProgressStatus"
                      label="In Bearbeitung"
                      dense
                      hide-details
                      class="ml-2"
                      dark></v-switch>
                  </div>
                  <v-btn color="primary" @click="submitComment" elevation="0">Senden <v-icon right small>mdi-send</v-icon></v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Attachment View Dialog -->
    <v-dialog v-model="attachementDialog" max-width="900" dark>
      <v-card color="black" dark>
        <v-img
          contain
          :src="currentTodoAttachments[selectedAttachementIndex] ? currentTodoAttachments[selectedAttachementIndex].url : ''"
          max-height="80vh"></v-img>
        <v-card-actions class="justify-center">
          <v-btn text @click="attachementDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video Link Dialog -->
    <v-dialog v-model="videoLinkDialog" max-width="400" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title>Video Link</v-card-title>
        <v-card-text>
          <v-text-field v-model="newVideoLink" label="YouTube Link" outlined dense autofocus dark></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="videoLinkDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="addVideoLink">Hinzufügen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Translation Dialog -->
    <v-dialog v-model="translationDialog" max-width="500px" dark>
      <v-card color="#1E1E1E" dark>
        <v-card-title class="headline">Übersetzung (Türkisch)</v-card-title>
        <v-card-text class="body-1 pt-4 white--text">
          {{ translatedDescription }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="translationDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"
import { mapState } from "vuex"
import userService from "@/services/userService"
import { saveAs } from "file-saver"
import socketService from "@/socketService"

export default {
  name: "toDoListMain",
  components: { userSearch },
  data() {
    return {
      userService,
      // State
      onlyUserTodos: false,
      viewMode: "list",
      activeTab: "techniker",
      sortBy: "priority", // 'priority' | 'date'
      selectedStudio: null,
      studios: [],

      headers: [
        { text: "Titel", value: "title" },
        { text: "Priorität", value: "priority" },
        { text: "Datum", value: "createdAt" },
        { text: "Studio", value: "studio.name" },
        { text: "Ersteller", value: "createdBy" },
        { text: "Zugewiesen", value: "assignedUsers" },
      ],

      // Search Queries map
      searchQueries: {
        techniker: "",
        marketing: "",
        coo: "",
        cl: "",
        it: "",
        resolved: "",
      },

      // Data Lists
      openTodos: [],
      resolvedTodos: [],

      // Dialogs
      todoDialog: false,
      todoDialogTitle: "",
      detailDialog: false,
      videoLinkDialog: false,
      translationDialog: false,
      attachementDialog: false,

      // Editing / Current Item
      currentTodo: null,
      editedTodo: {
        _id: null,
        title: "",
        description: "",
        priority: "medium",
        studio: null,
        plannedDate: null,
        plannedTime: null,
        category: "techniker",
        subcategory: null,
        plannedDate: null,
        plannedTime: null,
      },
      editMode: false,
      formValid: false,

      // Attachments
      currentTodoAttachments: [],
      selectedFile: null,
      selectedAttachementIndex: null,
      newVideoLink: "",

      // Assignment
      createdByUser: null,
      assignedUserKey: 0,
      newAssignedUser: null,
      showAssignment: false,

      // Comments
      newComment: "",
      isResolved: false,
      isMarketingOnlyComment: false,

      // Date/Time Pickers
      dateMenu: false,
      timeMenu: false,

      // Static Data
      priorityItems: ["low", "medium", "high"],
    }
  },
  beforeDestroy() {
    socketService.off("todo_updated")
  },
  async created() {
    this.setupSocketListeners()
    let studios = await api.fetchStudios()
    this.studios = studios
      .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
      .map((studio) => {
        return { _id: studio._id, name: studio.name }
      })
    this.selectedStudio = this.userStudio
    this.fetchOpenTodos()
  },
  watch: {
    async currentTodo(newVal) {
      // Clean up old URLs
      this.currentTodoAttachments.forEach((att) => {
        if (att.objectUrl) URL.revokeObjectURL(att.objectUrl)
      })
      this.currentTodoAttachments = []

      if (newVal && newVal.attachments) {
        for (let attachment of newVal.attachments) {
          try {
            if (attachment.type === "video") {
              this.currentTodoAttachments.push({ ...attachment, url: attachment.filePath })
            } else if (attachment.type === "image") {
              let base64Url = await api.toDoList.fetchAttachment(attachment.filePath, true)
              this.currentTodoAttachments.push({ ...attachment, url: base64Url })
            } else {
              const blob = await api.toDoList.fetchAttachment(attachment.filePath, false)
              const objectUrl = URL.createObjectURL(blob)
              this.currentTodoAttachments.push({ ...attachment, url: objectUrl, objectUrl: objectUrl })
            }
          } catch (error) {
            console.error("Fehler beim Laden des Anhangs:", error)
          }
        }
      }
      // Legacy images support if needed (omitted for brevity as attachments cover it, but keeping array init)
    },
    activeTab(newVal) {
      if (newVal === "resolved") {
        this.fetchResolvedTodos()
      } else {
        this.fetchOpenTodos()
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    userStudio() {
      return this.userService.getStudio()?._id || null
    },
    availableCategories() {
      let categories = [
        { text: "Techniker", value: "techniker" },
        { text: "Marketing", value: "marketing" },
      ]
      if (this.userService.hasRight("coo_toDoListItems")) categories.push({ text: "COO", value: "coo" })
      if (this.userService.hasRight("cl_toDoListItems")) categories.push({ text: "CL", value: "cl" })
      if (this.userService.hasRight("it_toDoListItems")) categories.push({ text: "IT", value: "it" })
      return categories
    },
    sortedITSubcategories() {
      const itSubcategories = [
        // --- Software & Services ---
        { text: "E-Mail / Office 365", value: "Email" },
        { text: "TAC (Mitgliedersoftware)", value: "TAC" },

        // --- Access & Security (Sehr wichtig im Gym) ---
        { text: "Zutrittskontrolle (Drehkreuze/Türen)", value: "AccessControl" },
        { text: "RFID-Leser / Check-In", value: "RFIDReaders" },
        { text: "Kamerasystem (CCTV)", value: "CCTV" },

        // --- Infrastructure & Network ---
        { text: "WLAN & Internet", value: "NetworkWifi" },
        { text: "Netzwerk (Router/Switch/Firewall)", value: "NetworkInfrastructure" },
        { text: "Telefonie (VoIP)", value: "Telephony" },

        // --- Hardware / Endpoints ---
        { text: "PC & Workstations", value: "Workstations" },
        { text: "Drucker & Scanner", value: "Printers" },

        { text: "Musikanlage / Sonos", value: "AudioSystem" },
        { text: "TV / Infoscreens", value: "DigitalSignage" },

        // --- Misc ---
        { text: "Allgemein IT", value: "GeneralIT" },
        { text: "Verkabelung", value: "Cabling" },
      ]

      return itSubcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    sortedCooSubcategories() {
      const subcategories = [
        { text: "Solarium", value: "Solarium" },
        { text: "Me-Scan", value: "Me-Scan" },
        { text: "Hydrojet", value: "Hydrojet" },
        { text: "Ausdauergeräte", value: "Ausdauergeräte" },
        { text: "Kraftgeräte", value: "Kraftgeräte" },
        { text: "Senso Pro", value: "Senso Pro" },
        { text: "Milon", value: "Milon" },
        { text: "Stretching", value: "Stretching" },
        { text: "Plate Loaded", value: "Plate Loaded" },
        { text: "Kabeltürme", value: "Kabeltürme" },
        { text: "Workout ", value: "Workout " },
        { text: "LH", value: "LH" },
        { text: "KH", value: "KH" },
        { text: "Racks", value: "Racks" },
        { text: "MP", value: "MP" },
        { text: "Functional", value: "Functional" },
        { text: "Group Fitness", value: "Group Fitness" },
        { text: "Wellness", value: "Wellness" },
        { text: "Getränkeanlage", value: "Getränkeanlage" },
        { text: "Power Plate", value: "Power Plate" },
        { text: "Kinderhort", value: "Kinderhort" },
        { text: "Pelvix Power", value: "Pelvix Power" },
        { text: "Shop", value: "Shop" },
        { text: "Abfall", value: "Abfall" },
        { text: "Allgemein", value: "Allgemein" },
        { text: "roody", value: "roody" },
        { text: "TAC", value: "TAC" },
        { text: "Schulungen", value: "Schulungen" },
      ]
      return subcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    sortedClSubcategories() {
      // Reusing the same list as requested/implied by previous logic, or specific CL ones
      const subcategories = [
        { text: "Solarium", value: "Solarium" },
        { text: "Me-Scan", value: "Me-Scan" },
        { text: "Hydrojet", value: "Hydrojet" },
        { text: "Ausdauergeräte", value: "Ausdauergeräte" },
        { text: "Kraftgeräte", value: "Kraftgeräte" },
        { text: "Senso Pro", value: "Senso Pro" },
        { text: "Milon", value: "Milon" },
        { text: "Stretching", value: "Stretching" },
        { text: "Plate Loaded", value: "Plate Loaded" },
        { text: "Kabeltürme", value: "Kabeltürme" },
        { text: "Workout ", value: "Workout " },
        { text: "LH", value: "LH" },
        { text: "KH", value: "KH" },
        { text: "Racks", value: "Racks" },
        { text: "MP", value: "MP" },
        { text: "Functional", value: "Functional" },
        { text: "Group Fitness", value: "Group Fitness" },
        { text: "Wellness", value: "Wellness" },
        { text: "Getränkeanlage", value: "Getränkeanlage" },
        { text: "Power Plate", value: "Power Plate" },
        { text: "Kinderhort", value: "Kinderhort" },
        { text: "Pelvix Power", value: "Pelvix Power" },
        { text: "Shop", value: "Shop" },
        { text: "Abfall", value: "Abfall" },
        { text: "Allgemein", value: "Allgemein" },
        { text: "roody", value: "roody" },
        { text: "TAC", value: "TAC" },
        { text: "Schulungen", value: "Schulungen" },
      ]
      return subcategories.sort((a, b) => a.text.localeCompare(b.text))
    },
    filteredResolvedTodos() {
      let list = this.resolvedTodos

      // Rights Checks
      if (!this.userService.hasRight("coo_toDoListItems")) list = list.filter((t) => t.category !== "coo")
      if (!this.userService.hasRight("cl_toDoListItems")) list = list.filter((t) => t.category !== "cl")
      if (!this.userService.hasRight("it_toDoListItems")) list = list.filter((t) => t.category !== "it")

      const query = (this.searchQueries["resolved"] || "").toLowerCase()
      if (query) {
        list = list.filter((t) => this.matchesTodo(t, query))
      }
      return list
    },
    groupedResolvedTodosFiltered() {
      const groups = {}
      this.filteredResolvedTodos.forEach((todo) => {
        const date = new Date(todo.createdAt)
        const year = date.getFullYear()
        const month = date.getMonth()
        if (!groups[year]) groups[year] = {}
        if (!groups[year][month]) groups[year][month] = { techniker: [], marketing: [], coo: [], cl: [], it: [] }

        if (groups[year][month][todo.category]) {
          groups[year][month][todo.category].push(todo)
        }
      })

      return Object.keys(groups)
        .sort((a, b) => b - a)
        .map((year) => {
          const months = Object.keys(groups[year])
            .sort((a, b) => b - a)
            .map((month) => {
              const grp = groups[year][month]
              const sorter = (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
              return {
                monthName: new Date(year, month).toLocaleString("default", { month: "long" }),
                techniker: grp.techniker.sort(sorter),
                marketing: grp.marketing.sort(sorter),
                coo: grp.coo.sort(sorter),
                cl: grp.cl.sort(sorter),
                it: grp.it.sort(sorter),
                totalCount: grp.techniker.length + grp.marketing.length + grp.coo.length + grp.cl.length + grp.it.length,
              }
            })
            .filter((m) => m.totalCount > 0)
          return { year, months }
        })
        .filter((y) => y.months.length > 0)
    },
  },
  methods: {
    setupSocketListeners() {
      socketService.off("todo_updated")
      socketService.on("todo_updated", (updatedTodo) => {
        this.$toast.info(`ToDo "${updatedTodo.title}" wurde aktualisiert!`)
        this.openTodos = this.openTodos.filter((todo) => todo._id !== updatedTodo._id)
        this.resolvedTodos = this.resolvedTodos.filter((todo) => todo._id !== updatedTodo._id)

        if (updatedTodo.resolved) {
          this.resolvedTodos.unshift(updatedTodo)
        } else {
          let priorityValue = this.getPriorityValue(updatedTodo.priority)
          this.openTodos.unshift({ ...updatedTodo, priorityValue })
        }
        if (this.currentTodo && this.currentTodo._id === updatedTodo._id) {
          this.currentTodo = updatedTodo
        }
      })
    },
    getPriorityValue(priority) {
      if (priority === "high") return 3
      if (priority === "medium") return 2
      return 1
    },
    shouldShowCategory(cat) {
      if (cat === "coo" && !this.userService.hasRight("coo_toDoListItems")) return false
      if (cat === "cl" && !this.userService.hasRight("cl_toDoListItems")) return false
      if (cat === "it" && !this.userService.hasRight("it_toDoListItems")) return false
      return true
    },
    getSortedTodos(category) {
      let list = this.openTodos.filter((t) => t.category === category)

      // Search Filter
      const query = (this.searchQueries[category] || "").toLowerCase()
      if (query) {
        list = list.filter((t) => this.matchesTodo(t, query))
      }

      // Sorting
      if (this.sortBy === "priority") {
        // Sort by Priority Value DESC (3 to 1), then Date DESC
        list.sort((a, b) => {
          if (b.priorityValue !== a.priorityValue) return b.priorityValue - a.priorityValue
          return new Date(b.createdAt) - new Date(a.createdAt)
        })
      } else {
        // Date DESC
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      }
      return list
    },
    setSort(type) {
      this.sortBy = type
    },
    toggleOnlyUserTodos() {
      this.onlyUserTodos = !this.onlyUserTodos
      this.reloadTodos()
    },
    matchesTodo(todo, query) {
      if (todo.title?.toLowerCase().includes(query)) return true
      if (todo.description?.toLowerCase().includes(query)) return true
      if (todo.studio?.name?.toLowerCase().includes(query)) return true
      if (todo.createdBy?.vorname?.toLowerCase().includes(query)) return true
      if (todo.createdBy?.nachname?.toLowerCase().includes(query)) return true
      return false
    },
    getPriorityColor(priority) {
      if (priority === "high") return "red"
      if (priority === "medium") return "orange"
      return "green"
    },
    getPriorityColorHex(priority) {
      if (priority === "high") return "#F44336" // Red 500
      if (priority === "medium") return "#FF9800" // Orange 500
      return "#4CAF50" // Green 500
    },
    mapPriorityName(priority) {
      if (priority === "high") return "Hoch"
      if (priority === "medium") return "Mittel"
      return "Niedrig"
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YY HH:mm") : ""
    },
    fetchOpenTodos() {
      api.toDoList
        .fetchAll({ status: "open", studio: this.selectedStudio, onlyUserTodos: this.onlyUserTodos })
        .then((data) => {
          this.openTodos = data.map((todo) => ({ ...todo, priorityValue: this.getPriorityValue(todo.priority) }))
          if (this.currentTodo && this.currentTodo._id) {
            const found = this.openTodos.find((t) => t._id === this.currentTodo._id)
            if (found) this.currentTodo = found
          }
        })
        .catch(console.error)
    },
    fetchResolvedTodos() {
      api.toDoList
        .fetchAll({ status: "resolved", studio: this.selectedStudio, onlyUserTodos: this.onlyUserTodos })
        .then((data) => {
          this.resolvedTodos = data.map((todo) => ({ ...todo, priorityValue: this.getPriorityValue(todo.priority) }))
          if (this.currentTodo && this.currentTodo._id) {
            const found = this.resolvedTodos.find((t) => t._id === this.currentTodo._id)
            if (found) this.currentTodo = found
          }
        })
        .catch(console.error)
    },
    reloadTodos() {
      if (this.activeTab === "resolved") this.fetchResolvedTodos()
      else this.fetchOpenTodos()
    },
    // Actions
    openCreateDialog() {
      this.todoDialogTitle = "Neuer ToDo Eintrag"
      this.createdByUser = null
      this.assignedUserKey++
      this.editedTodo = {
        _id: null,
        title: "",
        studio: this.userStudio,
        createdBy: null,
        description: "",
        priority: "low",
        category: this.activeTab !== "resolved" ? this.activeTab : "techniker",
        subcategory: null,
        plannedDate: null,
        plannedTime: null,
      }
      this.todoDialog = true
    },
    closeTodoDialog() {
      this.todoDialog = false
    },
    saveTodo() {
      if (this.editedTodo.title.trim() === "") return this.$toast.error("Titel ist Pflicht")
      if (this.editedTodo.description.trim() === "") return this.$toast.error("Beschreibung ist Pflicht")
      if (this.editedTodo.description.length < 30) return this.$toast.error("Beschreibung mind. 30 Zeichen")

      // Priority Auto-Low Logic for restricted users
      if (
        (this.editedTodo.category === "marketing" && !userService.hasRight("todo_fetchAllMarketing") && this.editedTodo.priority !== "low") ||
        (this.editedTodo.category === "cl" && !userService.hasRight("cl_toDoListItems") && this.editedTodo.priority !== "low") ||
        (this.editedTodo.category === "it" && !userService.hasRight("it_toDoListItems") && this.editedTodo.priority !== "low")
      ) {
        this.editedTodo.priority = "low"
        this.$toast.info("Priorität wird vom Fachbereich gesetzt")
      }

      if (this.editedTodo._id) {
        // Update via Dialog (rare case if reused for edit, but edit is mostly inline)
      } else {
        // Create
        if (!this.createdByUser) return this.$toast.error("Ersteller wählen")
        this.editedTodo.createdBy = this.createdByUser
        api.toDoList
          .create(this.editedTodo)
          .then((e) => {
            if (this.editedTodo.studio !== this.selectedStudio) {
              this.selectedStudio = this.editedTodo.studio
            }
            this.activeTab = this.editedTodo.category
            this.reloadTodos()
            this.openDetailDialog(e)
            this.todoDialog = false
          })
          .catch(console.error)
      }
    },
    openDetailDialog(item) {
      this.currentTodo = {
        plannedDate: item.plannedDate || null,
        plannedTime: item.plannedTime || null,
        inProgress: item.inProgress || false,
        ...item,
      }
      this.editMode = false
      this.newComment = ""
      this.isMarketingOnlyComment = false
      this.showAssignment = false
      this.isResolved = item.resolved
      this.detailDialog = true
    },
    closeDetailDialog() {
      this.detailDialog = false
    },
    toggleEditMode() {
      this.editMode = !this.editMode
      if (this.editMode && this.currentTodo) {
        this.editedTodo = { ...this.currentTodo, studio: this.currentTodo.studio?._id || this.currentTodo.studio }
      }
    },
    saveEdit() {
      const payload = {
        title: this.editedTodo.title,
        description: this.editedTodo.description,
        priority: this.editedTodo.priority,
        studio: this.editedTodo.studio,
        category: this.editedTodo.category,
        subcategory: this.editedTodo.subcategory,
      }
      api.toDoList
        .update(this.currentTodo._id, payload)
        .then(() => {
          this.editMode = false
          this.reloadTodos()
        })
        .catch(console.error)
    },
    deleteCurrentTodo() {
      if (confirm("Wirklich löschen?")) {
        api.toDoList
          .delete(this.currentTodo._id)
          .then(() => {
            this.closeDetailDialog()
            this.reloadTodos()
          })
          .catch(console.error)
      }
    },
    submitComment() {
      const payload = {
        comment: this.newComment,
        plannedDate: this.currentTodo.plannedDate,
        plannedTime: this.currentTodo.plannedTime,
        status: this.isResolved ? "resolved" : "opened",
        marketingOnly: this.isMarketingOnlyComment,
      }
      api.toDoList
        .addComment(this.currentTodo._id, payload)
        .then(() => {
          this.newComment = ""
          this.reloadTodos()
          this.$toast.success("Kommentar gespeichert")
        })
        .catch(console.error)
    },
    updateInProgressStatus() {
      api.toDoList
        .update(this.currentTodo._id, { inProgress: this.currentTodo.inProgress })
        .then(() => {
          this.reloadTodos()
        })
        .catch(() => (this.currentTodo.inProgress = !this.currentTodo.inProgress))
    },
    // Assignment
    updateCreatedByUser(u) {
      this.createdByUser = u
    },
    updateNewAssignedUser(u) {
      this.newAssignedUser = u
    },
    toggleAssignment() {
      this.showAssignment = !this.showAssignment
    },
    submitAssignment() {
      api.toDoList
        .assign(this.currentTodo._id, { assignment: { user: this.newAssignedUser, rights: "manage" } })
        .then(() => {
          this.newAssignedUser = null
          this.assignedUserKey++
          this.showAssignment = false
          this.reloadTodos()
        })
        .catch(console.error)
    },
    removeUser(id) {
      api.toDoList
        .removeAssignment(this.currentTodo._id, id)
        .then(() => {
          this.currentTodo.assignedUsers = this.currentTodo.assignedUsers.filter((u) => u._id !== id)
          this.reloadTodos()
        })
        .catch(console.error)
    },
    // Attachments & Utils
    copyTextToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => this.$toast.success("Kopiert!"))
    },
    async translateTitle() {
      if (!this.currentTodo?.title) return
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(this.currentTodo.title)}&langpair=de|tr`
      try {
        const res = await fetch(url)
        const data = await res.json()
        if (data?.responseData) {
          this.translatedDescription = data.responseData.translatedText
          this.translationDialog = true
        }
      } catch (e) {
        this.$toast.error("Übersetzung fehlgeschlagen")
      }
    },
    async translateText() {
      if (!this.currentTodo?.description) return
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(this.currentTodo.description)}&langpair=de|tr`
      try {
        const res = await fetch(url)
        const data = await res.json()
        if (data?.responseData) {
          this.translatedDescription = data.responseData.translatedText
          this.translationDialog = true
        }
      } catch (e) {
        this.$toast.error("Übersetzung fehlgeschlagen")
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.$el.querySelector("input").click()
    },
    onFileSelected(file) {
      if (!file) return
      const fd = new FormData()
      fd.append("file", file)
      api.toDoList
        .uploadAttachment(fd, this.currentTodo._id)
        .then(() => {
          this.$toast.success("Hochgeladen")
          this.reloadTodos()
        })
        .catch((e) => this.$toast.error("Upload Fehler"))
      this.selectedFile = null
    },
    openAttachment(att) {
      api.toDoList
        .fetchAttachment(att.filePath, false)
        .then((blob) => saveAs(blob, att.originalName))
        .catch(console.error)
    },
    getAttachmentIcon(type) {
      if (type === "pdf") return "mdi-file-pdf-box"
      if (type === "video") return "mdi-youtube"
      return "mdi-file-document"
    },
    deleteSelectedAttachment(id) {
      if (confirm("Anhang löschen?")) {
        api.toDoList.deleteAttachment({ _id: this.currentTodo._id, attachmentId: id }).then(() => this.reloadTodos())
      }
    },
    openAttachementDialog(idx) {
      this.selectedAttachementIndex = idx
      this.attachementDialog = true
    },
    addVideoLink() {
      if (!this.newVideoLink) return
      api.toDoList.addVideoLink(this.currentTodo._id, { videoLink: this.newVideoLink }).then(() => {
        this.videoLinkDialog = false
        this.newVideoLink = ""
        this.reloadTodos()
      })
    },
    isCurrentUser(id) {
      return this.userService.getUser()?._id === id
    },
    canEditPriority() {
      // Simplified check
      if (this.editedTodo.category === "marketing" && !this.userService.hasRight("todo_fetchAllMarketing")) return false
      if (this.editedTodo.category === "cl" && !this.userService.hasRight("cl_toDoListItems")) return false
      if (this.editedTodo.category === "it" && !this.userService.hasRight("it_toDoListItems")) return false
      return true
    },
    isCategoryDisabled(cat) {
      if (cat === "coo" && !this.userService.hasRight("coo_toDoListItems")) return true
      if (cat === "cl" && !this.userService.hasRight("cl_toDoListItems")) return true
      if (cat === "it" && !this.userService.hasRight("it_toDoListItems")) return true
      return false
    },
  },
}
</script>

<style scoped>
.text-truncate-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.text-truncate-3-lines {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.hover-bg:hover {
  background-color: #2c2c2c !important;
}

::v-deep .v-slide-group__prev {
  display: none !important;
}
</style>
